import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, AlertCircle } from "lucide-react";
import { useUserContext } from "../context/UserContext";
import { toast } from "react-toastify";

interface ZipCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (zipCode: string) => void;
  currentZipCode?: string;
  title?: string;
  description?: string;
}

export default function ZipCodeModal({
  isOpen,
  onClose,
  onSuccess,
  currentZipCode = "",
  title = "Enter Zip Code",
  description = "Enter your zip code to search nearby stores.",
}: ZipCodeModalProps) {
  const { user, updateUser } = useUserContext();
  const [zipCode, setZipCode] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Initialize zip code when modal opens
  useEffect(() => {
    if (isOpen) {
      setZipCode(currentZipCode);
      setError("");
    }
  }, [isOpen, currentZipCode]);

  const validateZipCode = (code: string): boolean => {
    const zipRegex = /^\d{5}$/;
    return zipRegex.test(code);
  };

  const handleZipCodeChange = (value: string) => {
    // Only allow digits and limit to 5 characters
    const sanitized = value.replace(/\D/g, "").slice(0, 5);
    setZipCode(sanitized);
    setError("");
  };

  const handleSave = async () => {
    if (!zipCode.trim()) {
      setError("Please enter a zip code");
      return;
    }

    if (!validateZipCode(zipCode)) {
      setError("Please enter a valid 5-digit zip code");
      return;
    }

    if (!user) {
      setError("User not found");
      return;
    }

    setIsSaving(true);
    try {
      // Update user with new zip code
      const updatedUser = { zipCode: zipCode.trim() };
      await updateUser(updatedUser);

      // Call the optional success callback
      if (onSuccess) {
        onSuccess(zipCode.trim());
      }

      onClose();
      toast.success("Zip code updated successfully");
    } catch (error) {
      console.error("Error updating zip code:", error);
      setError("Failed to update zip code");
      toast.error("Failed to update zip code");
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
      onPointerDown={onClose}
    >
      <div
        className="bg-white dark:bg-dark-800 rounded-xl p-6 max-w-sm w-full border border-gray-200 dark:border-dark-700"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button
            onPointerDown={onClose}
            disabled={isSaving}
            className="p-1 text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors cursor-pointer disabled:opacity-50"
            style={{ touchAction: "manipulation" }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-dark-300 mb-4">{description}</p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
              Zip Code
            </label>
            <input
              type="text"
              value={zipCode}
              onChange={(e) => handleZipCodeChange(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center font-mono bg-white dark:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-400"
              placeholder="Enter 5-digit zip code"
              maxLength={5}
              disabled={isSaving}
            />
            {error && (
              <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center gap-1">
                  <AlertCircle className="w-3 h-3 text-red-500 dark:text-red-400" />
                  <p className="text-red-700 dark:text-red-300 text-xs">{error}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              onPointerDown={handleSave}
              disabled={isSaving || !zipCode.trim()}
              className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              style={{ touchAction: "manipulation" }}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button
              onPointerDown={onClose}
              disabled={isSaving}
              className="flex-1 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-dark-200 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-dark-600 disabled:opacity-50 cursor-pointer"
              style={{ touchAction: "manipulation" }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
